import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { BannerNotificationType } from "../../components/SimpleBanner";
import ProductionInventoryAdjustmentResult from "../../components/forms/production/components/ProductionInventoryAdjustmentResult";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";

import NitrateAndPhosphateCalculator from "../../components/forms/production/components/NitrateAndPhosphateCalculator";

export function ManufacturedProductViewModel(logout, productId, addSnackbar) {
  const [loading, setLoading] = useState(true);
  const [savingProduction, setSavingProduction] = useState(false);
  const [productionProgressMessage, setProductionProgressMessage] = useState("Saving production…");
  const [inventoryAdjustments, setInventoryAdjustments] = useState(null);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);
  const [adjustmentsError, setAdjustmentsError] = useState(null);

  const fetchInventoryAdjustments = async function () {
    setAdjustmentsLoading(true);
    setAdjustmentsError(null);
    try {
      const result = await makeGetApiCall(`manufacturedProducts/${productId}/inventoryAdjustments`);
      if (!result.success || !Array.isArray(result.data?.adjustments)) {
        if (result.errorStatus === 401) logout();
        throw new Error("Could not load QuickBooks adjustment history. Please refresh to try again.");
      }
      setInventoryAdjustments(result.data.adjustments);
    } catch (error) {
      setInventoryAdjustments(null);
      setAdjustmentsError(error.message);
    } finally {
      setAdjustmentsLoading(false);
    }
  };

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [certifiedByOptions, setCertifiedByOptions] = useState([]);
  const [claimsOptions, setClaimsOptions] = useState([]);
  const [inProcessMonitoringHistory, setInProcessMonitoringHistory] = useState(
    []
  );
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [printLabelDetails, setPrintLabelDetails] = useState({
    production_date: null,
    product_plu: null,
    product_name: null,
    product_description: null,
    product_weight: null,
    product_lot: null,
    supervisor: null,
    product_allergens: null,
    barcodeHtml: null,
    barcode: null,
  });
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState([]);
  const [printType, setPrintType] = useState(0);
  const [manufacturedProduct, setManufacturedProduct] = useState({
    ingredients: [],
  });

  const getProductReportById = async function (productId) {
    const payload = {
      product_id: productId,
    };
    let callResult = await makePostApiCall(
      `manufacturedProducts/id/${productId}`,
      payload
    );

    if (callResult.success) {
      const manufacturedProduct = callResult.data;
      setManufacturedProduct(manufacturedProduct);

      if (manufacturedProduct.recipe_id > 0) {
        setSelectedRecipe({
          recipe: manufacturedProduct.selectedRecipe.recipe,
          recipeItems: manufacturedProduct.selectedRecipe.recipeItems,
        });
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoading(false);
  };

  const getCertifiedByOptionsForDropdown = async function () {
    let callResult = await makePostApiCall(`certifiedByOptionsForDropdown`);

    if (callResult.success) {
      setCertifiedByOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getClaimsForDropdown = async function () {
    let callResult = await makePostApiCall(`claimsForDropdown`);

    if (callResult.success) {
      setClaimsOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getAssemblyItemReceivingByBarcode = async function (search, page) {
    const payload = {
      manufacture: true,
      page: page,
      search: search,
    };
    let callResult = await makePostApiCall(`receivingInvoiceIndex`, payload);

    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const updateInProcessMonitoringHistory = async function (payload) {
    const callResult = await makePostApiCall(
      `inProcessHistoryOnNextStatus`,
      payload
    );

    if (callResult.success) {
      // callResult.data expected shape: { message, new_history, new_next_status }
      const newHistory = callResult.data?.new_history || null;
      const newNextStatus = callResult.data?.new_next_status || null;
      const previous_status_record =
        callResult.data?.previous_status_record || null;

      const normalized = {
        id: newHistory?.id ?? null,
        production_id:
          newHistory?.production_id ?? payload.production_id ?? null,
        in_process_status_id: newHistory?.in_process_status_id ?? null,
        in_process_status_name: newHistory?.in_process_status_name ?? null,
        next_process_status_id: newNextStatus?.id ?? null,
        next_process_status_name: newNextStatus?.name ?? null,
        previous_process_status_id: previous_status_record?.status_id ?? null,
        previous_process_status_name:
          previous_status_record?.status_name ?? null,
      };

      setInProcessMonitoringHistory(normalized);
      if (addSnackbar) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "History updated",
            SnackbarType.Success
          )
        );
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
        return;
      }
      if (addSnackbar) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "Could not update history",
            SnackbarType.Error
          )
        );
      }
      showErrorModal(callResult.data.message || "Could not fetch history");
    }
  };

  const fetchInProcessMonitoringHistoryByProductionId = async function (
    productionId
  ) {
    const callResult = await makeGetApiCall(
      `inProcessMonitoringHistoryCurrentAndNextStatus/${productionId}`
    );

    if (callResult.success) {
      setInProcessMonitoringHistory(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
        return;
      }
      showErrorModal("Could not fetch history");
    }
  };

  const getProductsList = async function (search) {
    const payload = {
      page: 1,
      search: search,
    };
    let callResult = await makePostApiCall(`products`, payload);

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getReceivingAssemblyItemInStockByName = async function (search) {
    let callResult = await makePostApiCall(
      `getReceivingAssemblyItemInStockByName`,
      {
        search: search,
      }
    );

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const updateManufacturedProduct = async function (productValues) {
    setSavingProduction(true);
    setProductionProgressMessage("Saving recipe ingredients…");
    try {
      // Persist actual recipe consumption before the completion request deducts inventory.
      if (selectedRecipe.recipe) {
        const ingredientsResult = await makePostApiCall("saveOrUpdateManufactureIngredients", {
          ...selectedRecipe,
          manufactured_product_id: productValues.manufactured_product_id,
        });
        if (!ingredientsResult.success) {
          if (ingredientsResult.errorStatus === 401) logout();
          showErrorModal(ingredientsResult.data?.message || "Could not save recipe ingredients. Production was not completed.");
          return ingredientsResult;
        }
      }
      setProductionProgressMessage(Number(productValues.status) === 2
        ? "Saving production and adjusting QuickBooks ingredients…" : "Saving production…");
      const result = await makePostApiCall("updateManufacturedProduct", productValues);
      setProductionProgressMessage("Loading adjustment results…");
      // Also read history after failures: some ingredients may already have been deducted.
      await fetchInventoryAdjustments();
      if (!result.success) {
        if (result.errorStatus === 401) logout();
        showErrorModal(result.data?.message || "Could not save production. Check adjustment history before retrying.");
        return result;
      }
      await getProductReportById(productValues.manufactured_product_id);
      const adjusted = result.data?.quickbooks_assembly_inventory_adjustments || [];
      showAdjustmentResults(adjusted);
      return result;
    } catch (error) {
      await fetchInventoryAdjustments();
      const message = "Could not confirm the save. Check adjustment history before retrying.";
      showErrorModal(message);
      return { success: false, data: { message } };
    } finally {
      setSavingProduction(false);
    }
  };

  const showAdjustmentResults = (results) => {
    const errors = results.filter((item) => ["failed", "pending"].includes(item.status));
    if (errors.length) {
      setEmptyModelOpen({ open: true, childComponent: <ProductionInventoryAdjustmentResult
        results={results} onClose={() => setEmptyModelOpen({ open: false, childComponent: null })}
      /> });
      return;
    }
    const changed = results.some((item) => item.status === "adjusted");
    const message = changed ? "Production saved. QuickBooks ingredient inventory was adjusted. View Adjustment History for details."
      : results.length ? "Production saved. QuickBooks deductions were already recorded; no duplicate deductions were made."
      : "Production saved.";
    if (addSnackbar) addSnackbar(new SnackbarData(generateUniqueNumber(), message, SnackbarType.Success));
  };

  const retryInventoryAdjustments = async function () {
    setSavingProduction(true);
    setProductionProgressMessage("Retrying incomplete QuickBooks ingredient adjustments…");
    try {
      const result = await makePostApiCall(`manufacturedProducts/${productId}/inventoryAdjustments/retry`, {});
      setProductionProgressMessage("Loading adjustment results…");
      await fetchInventoryAdjustments();
      if (!result.success) {
        if (result.errorStatus === 401) logout();
        showErrorModal(result.data?.message || "Could not retry inventory adjustments. Check adjustment history.");
        return result;
      }
      showAdjustmentResults(result.data?.quickbooks_assembly_inventory_adjustments || []);
      return result;
    } catch (error) {
      await fetchInventoryAdjustments();
      showErrorModal("Could not confirm the retry. Check adjustment history before retrying again.");
      return { success: false };
    } finally {
      setSavingProduction(false);
    }
  };

  const getManufacturedProductIngredients = async function (
    manufactedProductId
  ) {
    let callResult = await makePostApiCall(
      `getManufacturedProductIngredients/${manufactedProductId}`
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const addIngredientToManufacturedProduct = async function (
    ingredientValues,
    isEdit
  ) {
    let callResult = await makePostApiCall(
      isEdit
        ? "updateManufacturedProductIngredientById/" + ingredientValues.id
        : "addIngredientToManufacturedProduct",
      ingredientValues
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const deleteIngredientFromManufacturedProduct = async function (
    ingredientId
  ) {
    let callResult = await makePostApiCall(
      `deleteIngredientFromManufacturedProduct/${ingredientId}`
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    return callResult;
  };

  const addManualEntryForManufacturingProduct = async function (productValues) {
    let callResult = await makePostApiCall(
      `addManualEntryToManufacturedProduct`,
      productValues
    );
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getManufacturedProductLabel = async function (manufacturedProductId) {
    let callResult = await makePostApiCall(
      `getManufacturedProductLabel/${manufacturedProductId}`
    );
    if (callResult.success) {
      setPrintType(1);
      setPrintLabelDetails(callResult.data);

      setTimeout(() => {
        window.print();
      }, 1500);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getEmployeesForDropdown = async function (type, department) {
    const payload = { department: department, type: [type] };
    let callResult = await makePostApiCall(`employeesForDropdown`, payload);

    if (callResult.success) {
      if (type === 1) {
        setEmployeeOptions(callResult.data);
      } else {
        setSupervisorOptions(callResult.data);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchRecipesForDropdown = async function () {
    let callResult = await makePostApiCall(`recipesForDropdown`);

    if (callResult.success) {
      setRecipes(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchRecipeItems = async function (id) {
    let callResult = await makePostApiCall(`fetchRecipeForUse/${id}`);

    if (callResult.success) {
      setSelectedRecipe({
        recipe: callResult.data.recipe,
        recipeItems: [
          ...callResult.data.recipeItems,
          ...callResult.data.packagingItems,
        ],
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const printProductionCostReport = async function () {
    setPrintType(2);

    setTimeout(() => {
      window.print();
    }, 1500);
  };

  const getProductionBatchReport = async function (id) {
    let callResult = await makePostApiCall(`createProductionBatchReport/${id}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getLabelCheckReport = async function (id) {
    let callResult = await makePostApiCall(`createLabelCheckReport/${id}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getVacuumPackReport = async function (id) {
    let callResult = await makePostApiCall(`createVacuumPackReport/${id}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCookingAndCoolingReport = async function (id) {
    let callResult = await makePostApiCall(
      `createCookingAndCoolingReport/${id}`
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getMetalDetectorReport = async function (id) {
    let callResult = await makePostApiCall(`createMetalDetectorReport/${id}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const onGenerateNitrateAndPhosphateReport = async function (payload) {
    let callResult = await makePostApiCall(`createNitrateAndPhosphateReport`, {
      ...payload,
      product_name: manufacturedProduct.product_name,
    });

    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const openNitrateAndPhosphateCalculator = () => {
    setEmptyModelOpen({
      childComponent: (
        <NitrateAndPhosphateCalculator
          manufacturedProduct={manufacturedProduct}
          actionCall={onGenerateNitrateAndPhosphateReport}
        />
      ),
      open: true,
    });
  };

  const previousInProcessMonitoringHistory = async function (payload) {
    const callResult = await makePostApiCall(
      `inProcessHistoryOnPreviousStatus`,
      payload
    );

    if (callResult.success) {
      const newHistory = callResult.data?.new_history || null;
      const newNextStatus = callResult.data?.new_next_status || null;
      const previous_status_record =
        callResult.data?.previous_status_record || null;

      const normalized = {
        id: newHistory?.id ?? null,
        production_id:
          newHistory?.production_id ?? payload.production_id ?? null,
        in_process_status_id: newHistory?.in_process_status_id ?? null,
        in_process_status_name: newHistory?.in_process_status_name ?? null,
        next_process_status_id: newNextStatus?.id ?? null,
        next_process_status_name: newNextStatus?.name ?? null,
        previous_process_status_id: previous_status_record?.status_id ?? null,
        previous_process_status_name:
          previous_status_record?.status_name ?? null,
      };

      setInProcessMonitoringHistory(normalized);
      if (addSnackbar) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "History updated",
            SnackbarType.Success
          )
        );
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
        return;
      }
      if (addSnackbar) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "Could not update history",
            SnackbarType.Error
          )
        );
      }
      showErrorModal(callResult.data.message || "Could not update history");
    }
  };

  return {
    inventoryAdjustments, adjustmentsLoading, adjustmentsError, fetchInventoryAdjustments, savingProduction, productionProgressMessage, retryInventoryAdjustments,
    loading,
    addIngredientToManufacturedProduct,
    deleteIngredientFromManufacturedProduct,
    emptyModal,
    getManufacturedProductIngredients,
    getProductReportById,
    manufacturedProduct,
    setEmptyModelOpen,
    showSimpleNotification,
    setShowSimpleNotification,
    setSlideOver,
    slideOver,
    certifiedByOptions,
    getAssemblyItemReceivingByBarcode,
    getCertifiedByOptionsForDropdown,
    getProductsList,
    updateManufacturedProduct,
    getReceivingAssemblyItemInStockByName,
    addManualEntryForManufacturingProduct,
    getManufacturedProductLabel,
    printLabelDetails,
    employeeOptions,
    getEmployeesForDropdown,
    fetchRecipesForDropdown,
    recipes,
    fetchRecipeItems,
    selectedRecipe,
    setSelectedRecipe,
    showErrorModal,
    printType,
    printProductionCostReport,
    getProductionBatchReport,
    getLabelCheckReport,
    getVacuumPackReport,
    getCookingAndCoolingReport,
    getMetalDetectorReport,
    claimsOptions,
    getClaimsForDropdown,
    supervisorOptions,
    openNitrateAndPhosphateCalculator,
    inProcessMonitoringHistory,
    fetchInProcessMonitoringHistoryByProductionId,
    updateInProcessMonitoringHistory,
    previousInProcessMonitoringHistory,
  };
}
